import axios from "axios";
import { api } from "../config";
import getAuthUserStorage from "./getAuthUserStorage";
import { toast } from "react-toastify";
import i18n from "../i18n";
import translateApiError from "./translateApiError";

// default
axios.defaults.baseURL = api.API_URL;
// content type
axios.defaults.headers.post["Content-Type"] = "application/json";

// Retry configuration for serverless cold start
const RETRY_CONFIG = {
  maxRetries: 5,
  retryDelay: 2000, // 2 seconds delay between retries
  retryableStatuses: [500, 502, 503, 504], // Common serverless cold start errors
};

const authUser = getAuthUserStorage();

if (authUser)
  axios.defaults.headers.common["Authorization"] = "Bearer " + authUser.token;

// Helper function to clear auth data and redirect to login
export const handleTokenExpiration = (userRole) => {
  // Clear auth data from storage
  localStorage.removeItem("authUser"); // Adjust this key based on your storage implementation
  sessionStorage.removeItem("authUser"); // If you also use sessionStorage

  // Remove authorization header
  delete axios.defaults.headers.common["Authorization"];

  // Redirect to unified login page for all users
  window.location.href = "/login";
};

// intercepting to capture errors
axios.interceptors.response.use(
  function (response) {
    const data = response.data ? response.data : response;

    // If the response has a 'body' field that's a string, check if it's JSON
    if (data && data.body && typeof data.body === "string") {
      // Only try to parse if it looks like JSON (starts with { or [)
      const trimmedBody = data.body.trim();
      if (trimmedBody.startsWith("{") || trimmedBody.startsWith("[")) {
        try {
          const parsedBody = JSON.parse(data.body);
          // Return the parsed body content directly (unwrap one level)
          return parsedBody;
        } catch (e) {
          console.error("Failed to parse response body:", e);
          // If parsing fails, return the original data
          return data;
        }
      }
      // If body doesn't look like JSON (e.g., encrypted data), return as-is
      return data;
    }

    return data;
  },
  function (error) {
    // Any status codes that falls outside the range of 2xx cause this function to trigger
    let message;
    let shouldShowToast = true;

    // Check for token expiration first
    if (error?.response?.data?.code === "BEARER_TOKEN_EXPIRED") {
      const currentUser = getAuthUserStorage();
      const userRole = currentUser?.roles;

      message = i18n.t("apiErrors.session.expired");

      // Show toast
      toast.error(message, { autoClose: 3000 });

      // Handle token expiration and redirect
      handleTokenExpiration(userRole);

      return Promise.reject({
        message,
        code: "BEARER_TOKEN_EXPIRED",
        isTokenExpired: true,
        autoLogout: true,
      });
    }

    // Check for user status errors (INACTIVE, PENDING, EXPIRED)
    const userStatusCodes = [
      "USER_INACTIVE",
      "USER_PENDING",
      "USER_EXPIRED",
      "USER_NOT_ACTIVE",
    ];

    if (
      error?.response?.data?.code &&
      userStatusCodes.includes(error.response.data.code)
    ) {
      const currentUser = getAuthUserStorage();
      const userRole = currentUser?.roles;

      // Use the message from backend
      message = translateApiError(
        error.response.data,
        i18n.t("apiErrors.session.inactive")
      );

      // Show toast
      toast.error(message, { autoClose: 3000 });

      // Handle auto logout and redirect
      handleTokenExpiration(userRole);

      return Promise.reject({
        message,
        code: error.response.data.code,
        userStatus: error.response.data.userStatus,
        autoLogout: true,
      });
    }

    // Check for token blacklisted
    if (error?.response?.data?.code === "TOKEN_BLACKLISTED") {
      const currentUser = getAuthUserStorage();
      const userRole = currentUser?.roles;

      message = translateApiError(
        error.response.data,
        i18n.t("apiErrors.session.invalidated")
      );

      // Show toast
      toast.error(message, { autoClose: 3000 });

      // Handle token expiration and redirect
      handleTokenExpiration(userRole);

      return Promise.reject({
        message,
        code: "TOKEN_BLACKLISTED",
        autoLogout: true,
      });
    }
    if (
      typeof process !== "undefined" &&
      process.env &&
      process.env.NODE_ENV !== "production"
    ) {
      console.error("API Error:", error);
    }
    // Handle other status codes
    const errorStatus = error?.response?.status || error?.status;
    switch (errorStatus) {
      case 500:
        message = i18n.t("apiErrors.http.500");
        break;
      case 502:
        message = i18n.t("apiErrors.http.502");
        break;
      case 503:
        message = i18n.t("apiErrors.http.503");
        break;
      case 504:
        message = i18n.t("apiErrors.http.504");
        break;
      case 401:
        message = translateApiError(
          {
            code: error?.response?.data?.code,
            // Only the backend's message: axios's own "Request failed with
            // status code 401" would replace the default below.
            message: error?.response?.data?.message,
          },
          i18n.t("apiErrors.http.401")
        );
        shouldShowToast = false;
        break;
      case 404:
        message = i18n.t("apiErrors.http.404");
        break;
      case 403:
        message = i18n.t("apiErrors.http.403");
        break;
      case 400:
        message = translateApiError(
          {
            code: error?.response?.data?.code,
            message:
              (error?.response?.data?.errors &&
                error?.response?.data?.errors[0]) ||
              error?.response?.data?.message ||
              error?.message,
          },
          i18n.t("apiErrors.http.400")
        );
        break;
      case 422:
        message = i18n.t("apiErrors.http.422");
        break;
      default:
        // Prioritize specific errors array over generic message
        message = translateApiError(
          {
            code: error?.response?.data?.code,
            message:
              (error?.response?.data?.errors &&
                error?.response?.data?.errors[0]) ||
              error?.response?.data?.message ||
              error?.message,
          },
          i18n.t("apiErrors.http.default")
        );
    }

    // Don't show toast for retryable errors - withRetry will handle them
    const isRetryableError = RETRY_CONFIG.retryableStatuses.includes(errorStatus);
    if (isRetryableError) {
      shouldShowToast = false;
    }

    // Show toast for general errors (except 401 and retryable errors)
    if (shouldShowToast && message) {
      toast.error(message, { autoClose: 5000 });
    }

    return Promise.reject({
      message,
      status: error?.response?.status || error?.status,
      data: error?.response?.data,
      code: error?.response?.data?.code,
    });
  }
);

/**
 * Sets the default authorization
 * @param {*} token
 */
const setAuthorization = (token) => {
  axios.defaults.headers.common["Authorization"] = "Bearer " + token;
};

/**
 * Removes the authorization header
 */
const removeAuthorization = () => {
  delete axios.defaults.headers.common["Authorization"];
};

/**
 * Retry wrapper for axios requests to handle serverless cold starts
 * @param {Function} requestFn - The axios request function to execute
 * @param {Object} options - Options for retry behavior
 * @param {boolean} options.silent - If true, don't show toast on error
 * @param {number} retryCount - Current retry attempt (internal use)
 * @returns {Promise} - The response or throws error after max retries
 */
const withRetry = async (requestFn, options = {}, retryCount = 0) => {
  const { silent = false } = options;

  try {
    return await requestFn();
  } catch (error) {
    // Use status from interceptor's processed error or raw response
    const status = error?.status || error?.response?.status;
    const isRetryable = RETRY_CONFIG.retryableStatuses.includes(status);
    const hasRetriesLeft = retryCount < RETRY_CONFIG.maxRetries - 1;

    // Don't retry for auth-related errors or non-retryable statuses
    if (isRetryable && hasRetriesLeft) {
      if (
        typeof process !== "undefined" &&
        process.env &&
        process.env.NODE_ENV !== "production"
      ) {
        console.log(
          `API request failed with status ${status}. Retrying... (${retryCount + 1}/${RETRY_CONFIG.maxRetries})`
        );
      }

      // Wait before retrying
      await new Promise((resolve) =>
        setTimeout(resolve, RETRY_CONFIG.retryDelay)
      );

      return withRetry(requestFn, options, retryCount + 1);
    }

    // All retries exhausted or non-retryable error - show toast now (unless silent)
    // Use toastId to prevent duplicate toasts
    if (!silent && isRetryable && error?.message) {
      toast.error(error.message, {
        autoClose: 5000,
        toastId: `retry-error-${status}`
      });
    }

    throw error;
  }
};

class APIClient {
  /**
   * Fetches data from given url
   * @param {string} url - The URL to fetch
   * @param {Object} [params] - Query parameters (optional)
   * @param {Object} [options] - Options like { silent: true } to suppress error toast
   */
  get = (url, params = null, options = {}) => {
    let paramKeys = [];

    if (params) {
      Object.keys(params).map((key) => {
        paramKeys.push(key + "=" + params[key]);
        return paramKeys;
      });

      const queryString =
        paramKeys && paramKeys.length ? paramKeys.join("&") : "";
      return withRetry(() => axios.get(`${url}?${queryString}`, params), options);
    } else {
      return withRetry(() => axios.get(`${url}`), options);
    }
  };

  /**
   * post given data to url
   */
  create = (url, data = {}, options = {}) => {
    return withRetry(() => axios.post(url, data), options);
  };

  /**
   * Updates data
   */
  update = (url, data = {}, options = {}) => {
    return withRetry(() => axios.patch(url, data), options);
  };

  put = (url, data = {}, options = {}) => {
    return withRetry(() => axios.put(url, data), options);
  };

  /**
   * Delete
   */
  delete = (url, config = {}, options = {}) => {
    return withRetry(() => axios.delete(url, { ...config }), options);
  };
}

export { APIClient, setAuthorization, removeAuthorization };
